import classNames from "classnames";
import React, { FC, memo } from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "@/components/Icon";
import {
  ISelectOptionComponentProps,
  ISelectProps as Props,
  ReactSelectComponents,
  Select as EworkitSelect,
  SELECT_CLASS_NAME,
} from "@/eworkitComponents";

import { useSimpleValue } from "./useSimpleValue";

export interface ISelectProps<T = any> extends Props<T> {
  simpleValue?: boolean;
  onCreateOption?: (input: string) => void;
  createOptionPosition?: "first" | "last";
  getNewOptionData?: (input: string) => object;
}

export const SELECT_ICONS = {
  clear: <Icon icon="close" size="sm" color="grey2" />,
  dropdownMenuOpen: <Icon icon="dropup" size="md" color="grey2" />,
  dropdownMenuClosed: <Icon icon="dropdown" size="md" color="grey2" />,
};

export const Select = memo(<T,>(p: ISelectProps<T>) => {
  const { isClearable = true, onChange, value: v, valueKey = "code", ...props } = p;
  const { handleChange, value } = useSimpleValue({
    onChange: onChange as any,
    value: v as any,
    valueKey,
    ...p,
  });
  const { t } = useTranslation("common");

  return (
    <EworkitSelect
      isClearable={isClearable}
      onChange={handleChange}
      icons={SELECT_ICONS}
      noOptionsMessage={({ inputValue }) =>
        inputValue ? t("MESSAGES.NO_RESULTS_FOUND") : t("MESSAGES.TYPE_TO_SEARCH")
      }
      value={value}
      valueKey={valueKey}
      {...(props as any)}
    />
  );
});

export const SelectOption: FC<ISelectOptionComponentProps<any>> = memo(
  props => {
    const size = props.selectProps.size;
    const classes = classNames(props.className, {
      [`${SELECT_CLASS_NAME}__option--size-${size}`]: size,
    });
    return <ReactSelectComponents.Option {...props} className={classes} />;
  },
  /*
    This is needed to make Select more performant

    1. If you remove this and move the mouse up and down
       you will see Select is a bit laggy
    2. We can't remove onMouseMove and onMouseOver events
       because user will not be able yo use keyboard with Select
    3. Things that can be done to speed up Select if needed
       https://github.com/JedWatson/react-select/issues/3128
  */
  (prevProps, nextProps) => {
    return prevProps.isFocused === nextProps.isFocused;
  },
);
